import axios, { AxiosResponse, AxiosError } from 'axios';
import {
  ApiResponse,
  ApiSuccessResponse,
  ApiErrorResponse,
  isApiErrorResponse,
  isApiSuccessResponse
} from '../types/api';
import {
  extractErrorInfo,
  logError,
  isAuthenticationError,
  isAuthorizationError,
  shouldRetry,
  getNotificationMessage
} from '../utils/errorUtils';

// ============================================
// VERSION CHECK: Commit NEW - 2025-10-08
// ============================================
console.log('🚀🚀🚀 [api.ts] Loading api.ts module - NEW BUILD');
console.log('🚀 [api.ts] Timestamp:', new Date().toISOString());
console.log('🚀 [api.ts] This message confirms new build is running!');

// Alert for visual confirmation (will remove after debugging)
if (typeof window !== 'undefined' && import.meta.env.PROD) {
  console.log('⚠️ [api.ts] Production build detected - will show URL in console');
}

// API Configuration - Smart runtime detection with fallback
const getApiBaseUrl = (): string => {
  console.log('🔍 [API Config] Starting API URL detection...');
  console.log('🔍 [API Config] VITE_API_URL:', import.meta.env.VITE_API_URL);
  console.log('🔍 [API Config] window.location.hostname:', typeof window !== 'undefined' ? window.location.hostname : 'undefined');
  console.log('🔍 [API Config] window.location.protocol:', typeof window !== 'undefined' ? window.location.protocol : 'undefined');

  // 1. Check env var first (works if Railway injects it)
  if (import.meta.env.VITE_API_URL) {
    console.log('✅ Using VITE_API_URL:', import.meta.env.VITE_API_URL);
    return import.meta.env.VITE_API_URL;
  }

  // 2. Runtime detection for production (fallback if env var not available)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // If running on production domains with HTTPS, use HTTPS backend
    if ((hostname.includes('railway.app') || hostname.includes('thaisecuritydirectory.com')) && protocol === 'https:') {
      const url = 'https://thai-security-backend-production.up.railway.app';
      console.log('🌐 Production detected, using:', url);
      return url;
    }
  }

  // 3. Default to localhost for development
  console.log('💻 Development mode, using localhost');
  return 'http://localhost:7004';
};

const API_BASE_URL = getApiBaseUrl();
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '30000');

// Export for use in other files that don't use apiClient
export const API_URL = API_BASE_URL;
export { getApiBaseUrl };

// Debug log
console.log('🔧 Final API Base URL:', API_BASE_URL);

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  // No special redirect handling needed - backend now has redirect_slashes=False
});

// Request interceptor for JWT token authentication
apiClient.interceptors.request.use(
  (config) => {
    // Add JWT token to Authorization header if available
    // FIXED: Read from 'access_token' to match AuthContext.tsx line 193
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Remove Content-Type header for FormData to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    console.error('API Auth Interceptor Error:', error);
    return Promise.reject(error);
  }
);

// Request interceptor for logging
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    console.log('🔍 [Interceptor] baseURL:', config.baseURL);
    console.log('🔍 [Interceptor] Full URL:', config.baseURL + config.url);
    if (config.data) {
      console.log('Request Body:', JSON.stringify(config.data, null, 2));
    }
    // Log if Authorization header is present
    if (config.headers.Authorization) {
      const authHeader = String(config.headers.Authorization);
      console.log('Auth Header:', authHeader.substring(0, 20) + '...');
    }
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.config.url} - ${response.status}`);
    console.log('🔍 [Response] baseURL:', response.config.baseURL);
    console.log('🔍 [Response] Full URL:', response.config.baseURL + response.config.url);
    console.log('🔍 [Response] Request URL (from request):', response.request?.responseURL);
    return response;
  },
  (error: AxiosError) => {
    // Log error request details
    if (error.config) {
      console.error('❌ [Error] Request URL:', error.config.url);
      console.error('❌ [Error] baseURL:', error.config.baseURL);
      console.error('❌ [Error] Full URL:', error.config.baseURL + error.config.url);
    }

    // Log error for debugging
    logError(error, {
      endpoint: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      params: error.config?.params
    });

    // Extract standardized error information
    const processedError = extractErrorInfo(error);

    // BUG-020 FIX: Improved Network Error Detection & UX
    // Check for network errors (no response from server)
    if (error.code === 'ERR_NETWORK' || !error.response) {
      const networkError = new Error('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต');
      (networkError as any).code = 'NETWORK_ERROR';
      (networkError as any).type = 'network';
      (networkError as any).isRetryable = true;
      (networkError as any).suggestedAction = 'กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตของคุณแล้วลองอีกครั้ง';
      throw networkError;
    }

    // Check for timeout errors
    if (error.code === 'ECONNABORTED') {
      const timeoutError = new Error('การเชื่อมต่อหมดเวลา กรุณาลองใหม่อีกครั้ง');
      (timeoutError as any).code = 'TIMEOUT_ERROR';
      (timeoutError as any).type = 'timeout';
      (timeoutError as any).isRetryable = true;
      (timeoutError as any).suggestedAction = 'การเชื่อมต่อช้าเกินไป กรุณาลองใหม่อีกครั้ง';
      throw timeoutError;
    }

    // Check for server errors (500, 502, 503, 504)
    const status = error.response?.status;
    if (status && status >= 500) {
      const serverError = new Error('เซิร์ฟเวอร์ขัดข้อง กรุณาลองใหม่อีกครั้งในภายหลัง');
      (serverError as any).code = 'SERVER_ERROR';
      (serverError as any).type = 'server';
      (serverError as any).isRetryable = true;
      (serverError as any).suggestedAction = 'เซิร์ฟเวอร์กำลังประสบปัญหา กรุณารอสักครู่แล้วลองใหม่อีกครั้ง';
      throw serverError;
    }

    // Check for 404 errors
    if (status === 404) {
      const notFoundError = new Error('ไม่พบข้อมูลที่ต้องการ');
      (notFoundError as any).code = 'NOT_FOUND';
      (notFoundError as any).type = 'client';
      (notFoundError as any).isRetryable = false;
      (notFoundError as any).suggestedAction = 'ข้อมูลที่คุณต้องการไม่มีในระบบ';
      throw notFoundError;
    }

    // Handle authentication errors
    if (isAuthenticationError(error)) {
      console.warn('Authentication error detected:', processedError.message);

      // Clear all tokens from localStorage
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Redirect to appropriate login page
      const isAdminPage = window.location.pathname.startsWith('/admin');
      const loginPath = isAdminPage ? '/admin/login' : '/login';

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        console.log(`[Auth Error] Redirecting to ${loginPath}`);
        window.location.href = loginPath;
      }
    }

    // Handle authorization errors
    if (isAuthorizationError(error)) {
      console.warn('Authorization error detected:', processedError.message);
      const permissionError = new Error('คุณไม่มีสิทธิ์ในการเข้าถึงข้อมูลนี้');
      (permissionError as any).code = 'FORBIDDEN';
      (permissionError as any).type = 'authorization';
      (permissionError as any).isRetryable = false;
      (permissionError as any).suggestedAction = 'กรุณาติดต่อผู้ดูแลระบบหากคุณต้องการสิทธิ์นี้';
      throw permissionError;
    }

    // Create a standardized error object that components can handle
    const standardError = new Error(processedError.message);
    (standardError as any).code = processedError.code;
    (standardError as any).type = processedError.type;
    (standardError as any).isRetryable = processedError.isRetryable;
    (standardError as any).details = processedError.details;
    (standardError as any).fieldErrors = processedError.fieldErrors;
    (standardError as any).requestId = processedError.requestId;
    (standardError as any).suggestedAction = processedError.suggestedAction;

    throw standardError;
  }
);

// Types (updated to match FastAPI response format)
export interface Company {
  id: string;
  slug: string;
  nameTh: string;
  nameEn?: string;
  description: string;
  logoUrl?: string;
  phone1: string;
  phone2?: string;
  phone3?: string;
  email?: string;
  website?: string;
  lineId?: string;
  facebookUrl?: string;
  address: string;
  province: string;
  district: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  serviceTypes: string[];
  serviceAreas: string[];
  companySize: 'SMALL' | 'MEDIUM' | 'LARGE';
  establishedYear?: number;
  licenseNumber?: string;
  registrationId?: string;
  taxId?: string;
  isFeatured: boolean;
  isActive: boolean;
  isVerified: boolean;
  verifiedAt?: string;
  viewCount: number;
  contactCount: number;
  createdAt: string;
  updatedAt: string;
  // MAP FEATURE: Distance from user location (only in nearby search)
  distanceKm?: number;
}

// Ownership types
export interface Ownership {
  id: string;
  userId: string;
  companyId: string;
  isPrimary: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canTransfer: boolean;
  isActive: boolean;
  isApproved: boolean;
  approvedBy?: string;
  approvedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  company?: {
    id: string;
    slug: string;
    nameTh: string;
    nameEn?: string;
    province: string;
    companySize: string;
    isVerified: boolean;
    isFeatured: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

export interface OwnershipsResponse {
  ownerships: Ownership[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

// Legacy response format for companies - will be updated to use standard ApiResponse
export interface CompanyApiResponse<T> {
  companies?: T[];
  company?: T;
  total?: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
  has_next?: boolean;
  has_prev?: boolean;
  query?: string;
  filters?: any;
  search_time_ms?: number;
}

export interface ProvincesResponse {
  success: boolean;
  message: string;
  timestamp: string;
  data: {
    provinces: string[];
  };
}

export interface SearchFilters {
  province?: string;
  serviceType?: string;
  companySize?: string;
  featured?: boolean;
  page?: number;
  per_page?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface NearbySearchFilters {
  lat: number;
  lng: number;
  radius_km?: number;
  province?: string;
  service_types?: string[];
  company_size?: string;
  is_featured?: boolean;
  is_verified?: boolean;
  page?: number;
  per_page?: number;
}

// API Service Functions
export class ApiService {
  // Get companies with pagination
  static async getCompanies(page = 1, perPage = 20): Promise<CompanyApiResponse<Company>> {
    try {
      const response: AxiosResponse<CompanyApiResponse<Company>> = await apiClient.get(
        `/api/companies/?page=${page}&per_page=${perPage}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  }

  // Search companies
  static async searchCompanies(
    query: string,
    filters: SearchFilters = {}
  ): Promise<CompanyApiResponse<Company>> {
    try {
      const params = new URLSearchParams();

      if (query.trim()) {
        params.append('q', query.trim());
      }

      if (filters.province) {
        params.append('province', filters.province);
      }

      if (filters.serviceType) {
        params.append('service_types', filters.serviceType);
      }

      if (filters.companySize) {
        params.append('company_size', filters.companySize);
      }

      if (filters.featured !== undefined) {
        params.append('is_featured', String(filters.featured));
      }

      if (filters.sortBy) {
        params.append('sort_by', filters.sortBy);
      }

      if (filters.sortOrder) {
        params.append('sort_order', filters.sortOrder);
      }

      params.append('page', String(filters.page || 1));
      params.append('per_page', String(filters.per_page || 20));

      const response: AxiosResponse<CompanyApiResponse<Company>> = await apiClient.get(
        `/api/companies/search?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error searching companies:', error);
      throw error;
    }
  }

  // Get nearby companies (GPS-based search)
  static async getNearbyCompanies(
    filters: NearbySearchFilters
  ): Promise<CompanyApiResponse<Company>> {
    try {
      const params = new URLSearchParams();

      // Required parameters
      params.append('lat', String(filters.lat));
      params.append('lng', String(filters.lng));

      // Optional parameters
      if (filters.radius_km !== undefined) {
        params.append('radius_km', String(filters.radius_km));
      }

      if (filters.province) {
        params.append('province', filters.province);
        console.log('✅ [getNearbyCompanies] Added province to params:', filters.province);
      }

      if (filters.service_types && filters.service_types.length > 0) {
        filters.service_types.forEach(type => {
          params.append('service_types', type);
        });
      }

      // DEBUG: Log company_size filter value
      console.log('🔍 [getNearbyCompanies] company_size filter:', {
        raw_value: filters.company_size,
        type: typeof filters.company_size,
        will_be_added: !!filters.company_size
      });

      if (filters.company_size) {
        params.append('company_size', filters.company_size);
        console.log('✅ [getNearbyCompanies] Added company_size to params:', filters.company_size);
      } else {
        console.log('❌ [getNearbyCompanies] company_size filter NOT added (value is falsy)');
      }

      if (filters.is_featured !== undefined) {
        params.append('is_featured', String(filters.is_featured));
      }

      if (filters.is_verified !== undefined) {
        params.append('is_verified', String(filters.is_verified));
      }

      params.append('page', String(filters.page || 1));
      params.append('per_page', String(filters.per_page || 20));

      const finalUrl = `/api/companies/nearby?${params.toString()}`;
      console.log('📡 [getNearbyCompanies] Final API URL:', finalUrl);

      const response: AxiosResponse<CompanyApiResponse<Company>> = await apiClient.get(finalUrl);
      return response.data;
    } catch (error) {
      console.error('Error searching nearby companies:', error);
      throw error;
    }
  }

  // Get single company by slug
  static async getCompanyBySlug(slug: string): Promise<Company> {
    try {
      const response: AxiosResponse<Company> = await apiClient.get(
        `/api/companies/${slug}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  }

  // Get all provinces
  static async getProvinces(): Promise<string[]> {
    try {
      const response: AxiosResponse<ProvincesResponse> = await apiClient.get(
        `/api/provinces/`
      );
      return response.data.data.provinces;
    } catch (error) {
      console.error('Error fetching provinces:', error);
      throw error;
    }
  }

  // Health check
  static async healthCheck(): Promise<{ status: string }> {
    try {
      const response: AxiosResponse<{ status: string }> = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw error;
    }
  }

  // Track page view (optional - for analytics)
  static async trackPageView(companyId: string, path: string): Promise<void> {
    try {
      await apiClient.post('/api/tracking/view', {
        company_id: companyId,
        path,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        user_agent: navigator.userAgent,
        referrer: document.referrer || '',
      });
    } catch (error) {
      // Don't throw error for tracking failures
      console.warn('Failed to track page view:', error);
    }
  }

  // Track search (optional - for analytics)
  static async trackSearch(
    query: string,
    resultsCount: number,
    filters?: SearchFilters,
    searchTimeMs?: number
  ): Promise<void> {
    try {
      await apiClient.post('/api/tracking/search', {
        query,
        results_count: resultsCount,
        filters: filters || {},
        search_time_ms: searchTimeMs,
        timestamp: new Date().toISOString(),
        session_id: this.getSessionId(),
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      // Don't throw error for tracking failures
      console.warn('Failed to track search:', error);
    }
  }

  // Track contact interaction (optional - for analytics)
  static async trackContact(
    companyId: string,
    contactType: 'phone' | 'email' | 'website' | 'line' | 'facebook',
    contactValue?: string
  ): Promise<void> {
    try {
      await apiClient.post('/api/tracking/contact', {
        company_id: companyId,
        interaction_type: contactType,  // Changed from contact_type to interaction_type
        contact_value: contactValue,
        source_page: window.location.pathname,  // Added source_page
        referrer: document.referrer || undefined,  // Added referrer
        session_id: this.getSessionId(),
        // Removed timestamp and user_agent as backend handles these automatically
      });
    } catch (error) {
      // Don't throw error for tracking failures
      console.warn('Failed to track contact:', error);
    }
  }

  // Get user's company ownerships
  static async getOwnerships(page = 1, perPage = 20): Promise<OwnershipsResponse> {
    try {
      const response: AxiosResponse<OwnershipsResponse> = await apiClient.get(
        `/api/ownership/claims`,
        { params: { page, per_page: perPage } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching ownerships:', error);
      throw error;
    }
  }

  // Check company ownership status with detailed information
  static async checkCompanyOwnership(companyId: string): Promise<{
    status: 'none' | 'pending' | 'approved' | 'rejected';
    canClaim: boolean;
    isOwner?: boolean;
    message: string;
    claimedAt?: string;
    approvedAt?: string;
    rejectedAt?: string;
  }> {
    try {
      const response = await apiClient.get(`/api/ownership/check/${companyId}`);
      return response.data;
    } catch (error) {
      console.error('Error checking company ownership:', error);
      // Return default "none" status on error (e.g., 401 for guests)
      return {
        status: 'none',
        canClaim: true,
        message: 'คุณสามารถ claim บริษัทนี้ได้'
      };
    }
  }

  // Generate or get session ID
  private static getSessionId(): string {
    const storageKey = 'thai_security_session_id';
    let sessionId = sessionStorage.getItem(storageKey);

    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(storageKey, sessionId);
    }

    return sessionId;
  }
}

// Admin User Management API
export interface AdminUserCreateRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: 'ADMIN' | 'MEMBER';
  phone?: string;
  lineId?: string;
}

export interface AdminUserResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  full_name: string;
  display_name: string;
  phone?: string;
  lineId?: string;
  role: string;
  status: string;
  profileCompletionPercent: number;
  lastLoginAt?: string;
  loginCount: number;
  createdAt: string;
  updatedAt: string;
  adminNotes?: string;
}

export class AdminUserService {
  /**
   * สร้างผู้ใช้ใหม่โดย Admin
   * @param userData - ข้อมูลผู้ใช้ที่ต้องการสร้าง
   * @returns ข้อมูลผู้ใช้ที่สร้างสำเร็จ
   */
  static async createUser(userData: AdminUserCreateRequest): Promise<AdminUserResponse> {
    try {
      const response = await apiClient.post<AdminUserResponse>(
        '/api/admin/users',
        userData
      );
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  /**
   * ดึงรายการผู้ใช้ทั้งหมด
   */
  static async getUsers(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    role?: string;
    status?: string;
  }) {
    try {
      const response = await apiClient.get('/api/admin/users', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  /**
   * ดึงข้อมูลผู้ใช้ตาม ID
   */
  static async getUserById(userId: string): Promise<AdminUserResponse> {
    try {
      const response = await apiClient.get<AdminUserResponse>(`/api/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  /**
   * อัปเดตข้อมูลผู้ใช้
   */
  static async updateUser(userId: string, userData: Partial<AdminUserCreateRequest>): Promise<AdminUserResponse> {
    try {
      const response = await apiClient.put<AdminUserResponse>(`/api/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * ลบผู้ใช้ (Soft Delete)
   */
  static async deleteUser(userId: string) {
    try {
      const response = await apiClient.delete(`/api/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * เปลี่ยน Role ผู้ใช้
   */
  static async updateUserRole(userId: string, role: 'ADMIN' | 'MEMBER', adminNotes?: string) {
    try {
      const response = await apiClient.put(`/api/admin/users/${userId}/role`, {
        role,
        adminNotes
      });
      return response.data;
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  /**
   * เปิดใช้งานผู้ใช้
   */
  static async activateUser(userId: string) {
    try {
      const response = await apiClient.post(`/api/admin/users/${userId}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  }

  /**
   * ปิดใช้งานผู้ใช้
   */
  static async deactivateUser(userId: string) {
    try {
      const response = await apiClient.post(`/api/admin/users/${userId}/deactivate`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating user:', error);
      throw error;
    }
  }
}

export default ApiService;
export { apiClient };